import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useNavigate } from 'react-router-dom';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, User, Package, CornerDownLeft, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchedOrder, OrderSearchMode } from '@/supabase/order/types/orderSearch';
import { useUnifiedOrderSearch } from "@/hooks/useUnifiedOrderSearch";
import { renderHighlighted } from "@/lib/highlightMatch";
import { SearchResultOrderCard } from "@/components/SearchResultOrderCard";

const LISTBOX_ID = "dashboard-search-listbox";

// flat 활성 인덱스(고객 → 상품 순)를 실제 option id로 변환 — aria-activedescendant가 이 id를 가리킴.
function optionIdForFlat(flatIndex: number, customerCount: number): string {
  return flatIndex < customerCount
    ? `dsopt-customer-${flatIndex}`
    : `dsopt-product-${flatIndex - customerCount}`;
}

// 제안 행 공통 클래스 — active(키보드) vs default→hover(마우스)를 JS isActive로 배타 적용.
const rowClass = (isActive: boolean) => cn(
  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-150",
  "cursor-pointer focus:outline-none",
  isActive
    ? "bg-indigo-50 text-indigo-900"      // keyboard-active
    : "text-gray-800 hover:bg-gray-100",  // default → hover
);

// 빈 결과 문구 — searchMode별 분기.
function emptyResultMessage(mode: OrderSearchMode, query: string): string {
  const q = query.trim();
  switch (mode) {
    case 'nickname':
      // 기존 닉네임 검색 문구 보존 (승인 플랜 명시 — 무회귀 디폴트)
      return '해당 닉네임에 대한 주문 내역이 없습니다.';
    case 'product':
      return `'${q}' 상품이 포함된 주문이 없습니다.`;
    case 'all':
    default:
      return `'${q}'와(과) 일치하는 주문이 없습니다. 닉네임·상품명을 다시 확인해주세요.`;
  }
}

export default function UnifiedOrderSearchCard() {
  const navigate = useNavigate();
  const {
    searchQuery,
    suggestions,
    isSuggesting,
    showSuggestions,
    closeSuggestions,
    openSuggestions,
    onQueryChange,
    searchByNickname,
    searchByProduct,
    searchUnified,
    result,
    isLoading,
    error,
  } = useUnifiedOrderSearch();

  const customerSuggestions = suggestions.customers;
  const productSuggestions = suggestions.products;
  const hasCustomerGroup = customerSuggestions.length > 0;
  const hasProductGroup = productSuggestions.length > 0;
  const totalSuggestions = customerSuggestions.length + productSuggestions.length;

  // 키보드 active 인덱스(flat) — 마우스 hover와 배타. 제안 목록 변경 시 -1로 리셋.
  const [activeIndex, setActiveIndex] = useState(-1);
  useEffect(() => { setActiveIndex(-1); }, [suggestions]);
  // 드롭다운이 닫히면 active 리셋 — 재오픈 시 stale activedescendant 방지 (codexloop PR3-R1 M4).
  useEffect(() => { if (!showSuggestions) setActiveIndex(-1); }, [showSuggestions]);

  const executeActive = useCallback((flatIndex: number) => {
    if (flatIndex < customerSuggestions.length) {
      searchByNickname(customerSuggestions[flatIndex]);
    } else {
      searchByProduct(productSuggestions[flatIndex - customerSuggestions.length].name);
    }
  }, [customerSuggestions, productSuggestions, searchByNickname, searchByProduct]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 IME — 모든 키 핸들러 공통 가드

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showSuggestions && totalSuggestions > 0) { openSuggestions(); setActiveIndex(0); return; }
      if (totalSuggestions === 0) return;
      setActiveIndex(i => Math.min(totalSuggestions - 1, i + 1)); // 끝에서 wrap 없음
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showSuggestions) return; // 닫힌 상태 ArrowUp 무시 — ArrowDown만 재오픈 (codexloop PR3-R1 M4)
      if (totalSuggestions === 0) return;
      setActiveIndex(i => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      // !isSuggesting 게이트: 로딩 중엔 옵션이 숨겨진 채 activeIndex가 이전 목록을 가리키므로
      // 보이지 않는 stale 항목 선택 대신 현재 입력으로 전체 검색 (Codex 봇 PR#104 P2 수용)
      if (showSuggestions && !isSuggesting && activeIndex >= 0 && activeIndex < totalSuggestions) {
        executeActive(activeIndex);
      } else {
        searchUnified(searchQuery);
      }
      return;
    }
    if (e.key === 'Escape') {
      closeSuggestions();
      return;
    }
  }, [showSuggestions, isSuggesting, totalSuggestions, activeIndex, openSuggestions, closeSuggestions, executeActive, searchUnified, searchQuery]);

  const handleOpenOrder = useCallback((order: SearchedOrder) => {
    navigate(`/order?date=${order.order_date}&nickname=${encodeURIComponent(order.nickname)}`);
  }, [navigate]);

  const renderResults = () => {
    if (!result) return null;
    if (result.orders.length === 0) {
      return <p className="text-sm text-gray-500">{emptyResultMessage(result.mode, result.query)}</p>;
    }
    return (
      <div className="space-y-4">
        {/* product/all 모드 결과 헤더 — 어떤 검색어로 몇 건인지 명시. nickname 모드는 자명해서 생략 (codexloop PR3-R1 M9).
            isPartial여도 건수에 '+' 미표기 — partial 배너가 잘림을 별도로 안내. */}
        {result.mode !== 'nickname' && (
          <p className="text-sm text-gray-500">'{result.query}' 검색 결과 {result.orders.length}건</p>
        )}
        {result.orders.map(order => (
          <SearchResultOrderCard
            key={order.id}
            order={order}
            mode={result.mode}
            query={result.query}
            onOpen={handleOpenOrder}
          />
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>닉네임 또는 상품명으로 주문 검색</CardTitle>
        <CardDescription>고객 닉네임 또는 상품명으로 최근 90일 + 예약 30일 주문을 검색합니다.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <div className="relative flex-grow">
            <Input
              type="text"
              role="combobox"
              aria-label="닉네임 또는 상품명으로 주문 검색"
              aria-expanded={showSuggestions}
              aria-controls={showSuggestions ? LISTBOX_ID : undefined}
              aria-activedescendant={showSuggestions && !isSuggesting && activeIndex >= 0 && activeIndex < totalSuggestions ? optionIdForFlat(activeIndex, customerSuggestions.length) : undefined}
              aria-autocomplete="list"
              autoComplete="off"
              placeholder="닉네임 또는 상품명 검색..."
              value={searchQuery}
              onChange={(e) => onQueryChange(e.target.value)}
              onBlur={() => setTimeout(() => closeSuggestions(), 150)}
              onKeyDown={handleKeyDown}
              className="pr-10"
            />
            {isSuggesting ? (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" aria-hidden="true" />
            ) : (
              <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            )}
            {showSuggestions && (
              <div className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
                <div className="max-h-72 overflow-y-auto py-1">
                  {/* listbox는 그룹+옵션만 감쌈 — 로딩/힌트/footer는 밖으로 (codexloop PR3-R1 M5).
                      팝업이 닫히면 aria-controls도 제거해 IDREF가 깨지지 않게 (codexloop PR3-R2 #3). */}
                  <div id={LISTBOX_ID} role="listbox" aria-label="검색 제안">
                    {!isSuggesting && hasCustomerGroup && (
                      <div role="group" aria-labelledby="dsgroup-customer">
                        <div id="dsgroup-customer" className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-xs font-semibold text-gray-500">
                          <User className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                          고객
                          <span className="ml-1 font-normal tabular-nums text-gray-400">{customerSuggestions.length}</span>
                        </div>
                        {customerSuggestions.map((name, i) => {
                          const isActive = activeIndex === i;
                          return (
                            <div
                              key={`c-${i}-${name}`}
                              role="option"
                              id={`dsopt-customer-${i}`}
                              aria-selected={isActive}
                              className={rowClass(isActive)}
                              onMouseDown={() => searchByNickname(name)}
                              onMouseMove={() => setActiveIndex(i)}
                            >
                              <User className={cn("h-4 w-4 shrink-0", isActive ? "text-indigo-500" : "text-gray-400")} aria-hidden="true" />
                              <span className="truncate">{renderHighlighted(name, searchQuery)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!isSuggesting && hasProductGroup && (
                      <div role="group" aria-labelledby="dsgroup-product">
                        <div
                          id="dsgroup-product"
                          className={cn(
                            "flex items-center gap-1.5 px-3 pb-1 text-xs font-semibold text-gray-500",
                            hasCustomerGroup ? "mt-1 border-t border-gray-100 pt-2" : "pt-2",
                          )}
                        >
                          <Package className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                          상품
                          <span className="ml-1 font-normal tabular-nums text-gray-400">{productSuggestions.length}</span>
                        </div>
                        {productSuggestions.map((product, i) => {
                          const flat = customerSuggestions.length + i;
                          const isActive = activeIndex === flat;
                          return (
                            <div
                              key={`p-${i}-${product.name}`}
                              role="option"
                              id={`dsopt-product-${i}`}
                              aria-selected={isActive}
                              className={cn(rowClass(isActive), "justify-between")}
                              onMouseDown={() => searchByProduct(product.name)}
                              onMouseMove={() => setActiveIndex(flat)}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <Package className={cn("h-4 w-4 shrink-0", isActive ? "text-indigo-500" : "text-gray-400")} aria-hidden="true" />
                                <span className="truncate">{renderHighlighted(product.name, searchQuery)}</span>
                              </span>
                              <span className="shrink-0 pl-3 text-xs tabular-nums text-gray-400">{product.lastOrderDate}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {isSuggesting && (
                    <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden="true" />
                      검색 중...
                    </div>
                  )}

                  {!isSuggesting && !hasCustomerGroup && !hasProductGroup && searchQuery.trim() && (
                    <button
                      type="button"
                      // Tab 진입 시 blur 타이머(150ms)가 팝업을 닫아 focus가 증발 — Enter와 중복 affordance라 mouse-only (codexloop PR3-R2 #4)
                      tabIndex={-1}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-500 transition-colors hover:bg-gray-100"
                      onMouseDown={() => searchUnified(searchQuery)}
                    >
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                      <span>일치하는 제안이 없어요. <span className="font-medium text-gray-700">Enter</span>로 전체 검색</span>
                    </button>
                  )}

                  {!isSuggesting && (hasCustomerGroup || hasProductGroup) && (
                    <div className="mt-1 flex items-center gap-1.5 border-t border-gray-100 px-3 py-1.5 text-xs text-gray-400">
                      <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
                      {activeIndex >= 0 ? (
                        <span><span className="font-medium text-gray-500">Enter</span>로 선택 · <span className="font-medium text-gray-500">Esc</span> 닫기</span>
                      ) : (
                        <span><span className="font-medium text-gray-500">Enter</span>로 전체 검색</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <Button onClick={() => searchUnified(searchQuery)}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "검색"}
          </Button>
        </div>
        <div className="mt-6">
          {isLoading && <div className="flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>}
          {error && <p className="text-red-500">오류가 발생했습니다: {error.message}</p>}
          {!isLoading && !error && result?.isPartial && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
              {/* mode-aware: nickname은 검색어 구체화 조언이 실행 불가라 생략, product/all만 부연 (codexloop PR3-R1 L10). */}
              <span>결과가 많아 일부만 표시되었습니다.{(result.mode === 'product' || result.mode === 'all') && ' 검색어를 더 구체적으로 입력해보세요.'}</span>
            </div>
          )}
          {!isLoading && !error && renderResults()}
        </div>
      </CardContent>
    </Card>
  );
}
